import { ReactNode, Suspense, lazy } from 'react';
import { Navigate, RouteObject, defer, useLocation, useParams } from 'react-router-dom';
import { Endpoint } from '@/Config/api/endpoint';
import client from '@/Config/api/queryClient';
import { QueryKey } from '@/Config/api/queryKey';
import { IndexPath, SettingsTabsEnum } from '@/Config/app/router/index.path';
import { UserApi } from '@/Domain/api/User/User.api';
import Default from '@/Domain/components/App/Layout/DefaultLayout';
import { NotFound } from '@/Domain/components/App/NotFound';
import { RouteFallback } from '@/Domain/components/App/RouteFallback';
import { GettingStartedError } from '@/UI/GettingStarted/GettingStartedError';
import { MAX_DATASETS } from '@/UI/Simulation/components/simulationUtils';
import { WEBSITE_VIEW_QUERY_PARAM, WebsiteView } from '@/UI/Website/viewMode';

const Conversation = lazy(() =>
  import('@/Domain/components/Inbox/Conversation/Conversation').then((module) => ({
    default: module.Conversation,
  })),
);
const ArticleSheet = lazy(() =>
  import('@/Domain/components/KnowledgeBase/ArticleSheet/Article.sheet').then((module) => ({
    default: module.ArticleSheet,
  })),
);
const SonyDocumentSheet = lazy(() =>
  import('@/Domain/components/KnowledgeBase/SonyDocumentSheet/SonyDocument.sheet').then(
    (module) => ({ default: module.SonyDocumentSheet }),
  ),
);
const AcceptInvitationPage = lazy(() =>
  import('@/UI/AcceptInvitation/AcceptInvitation.page').then((module) => ({
    default: module.AcceptInvitationPage,
  })),
);
const ActionsLoader = lazy(() =>
  import('@/UI/Actions/Actions.loader').then((module) => ({ default: module.ActionsLoader })),
);
const GettingStartedPage = lazy(() =>
  import('@/UI/GettingStarted/GettingStarted.page').then((module) => ({
    default: module.GettingStartedPage,
  })),
);
const HubspotConnectAgentPage = lazy(() =>
  import('@/UI/HubspotConnect/HubspotConnectAgent.page').then((module) => ({
    default: module.HubspotConnectAgentPage,
  })),
);
const IdentityPage = lazy(() =>
  import('@/UI/Identity/Identity.page').then((module) => ({ default: module.IdentityPage })),
);
const InboxPage = lazy(() =>
  import('@/UI/Inbox/Inbox.page').then((module) => ({ default: module.InboxPage })),
);
const InsightsPage = lazy(() =>
  import('@/UI/Insights/Insights.page').then((module) => ({ default: module.InsightsPage })),
);
const IntegrationsPage = lazy(() => import('@/UI/Integrations'));
const KnowledgeBasePage = lazy(() =>
  import('@/UI/KnowledgeBase/KnowledgeBase.page').then((module) => ({
    default: module.KnowledgeBasePage,
  })),
);
const SonyDocumentsPage = lazy(() =>
  import('@/UI/KnowledgeBase/SonyDocuments/SonyDocuments.page').then((module) => ({
    default: module.SonyDocumentsPage,
  })),
);
const ShopifyConnectAgentPage = lazy(() =>
  import('@/UI/ShopifyConnect/ShopifyConnectAgent.page').then((module) => ({
    default: module.ShopifyConnectAgentPage,
  })),
);
const SimulationPage = lazy(() =>
  import('@/UI/Simulation/Simulation.page').then((module) => ({ default: module.SimulationPage })),
);
const SettingsPage = lazy(() =>
  import('@/UI/User/Settings/Settings.page').then((module) => ({ default: module.SettingsPage })),
);
const WidgetSettingsPage = lazy(() =>
  import('@/UI/Website/WidgetSettings.page').then((module) => ({
    default: module.WidgetSettingsPage,
  })),
);

function suspended(page: JSX.Element, fallback: ReactNode = <RouteFallback />): JSX.Element {
  return <Suspense fallback={fallback}>{page}</Suspense>;
}

function RedirectConversationToInbox() {
  const { id } = useParams();
  const to = `../${IndexPath.inbox.root}/${id ?? ''}`;
  return <Navigate to={to} replace />;
}

function RedirectToSettings({ tab }: { tab: SettingsTabsEnum }) {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  params.set('tab', tab);
  return <Navigate to={`../${IndexPath.settings}?${params.toString()}`} replace />;
}

export const routes: RouteObject[] = [
  {
    errorElement: <NotFound />,
    children: [
      {
        // Literal path beats the `:scenario` sibling below by route-match
        // scoring (static segments outrank dynamic params in v6), so the
        // order here is for readability, not correctness.
        path: IndexPath.acceptInvitation,
        element: suspended(<AcceptInvitationPage />, <RouteFallback fullScreen />),
      },
      {
        path: IndexPath.hubspotConnect,
        element: suspended(<HubspotConnectAgentPage />, <RouteFallback fullScreen />),
      },
      {
        path: IndexPath.shopifyConnect,
        element: suspended(<ShopifyConnectAgentPage />, <RouteFallback fullScreen />),
      },
      {
        path: ':scenario',
        children: [
          {
            element: <Default />,
            children: [
              {
                index: true,
                element: <Navigate to={IndexPath.aiPreview} replace />,
              },
              {
                path: IndexPath.gettingStarted,
                element: suspended(<GettingStartedPage />),
                // The shared NotFound element redirects instead of rendering.
                errorElement: <GettingStartedError />,
                handle: { indexPath: IndexPath.gettingStarted },
              },
              {
                path: IndexPath.aiPreview,
                element: <div />,
                handle: { indexPath: IndexPath.aiPreview },
              },
              {
                path: IndexPath.knowledgeBase.root,
                element: suspended(<KnowledgeBasePage />),
                handle: { indexPath: IndexPath.knowledgeBase.root },
                children: [
                  {
                    // Nested route to open Article Sheet over Knowledge Base
                    path: 'articles/:id',
                    element: suspended(<ArticleSheet />, null),
                    handle: { indexPath: IndexPath.knowledgeBase.root },
                  },
                ],
              },
              {
                path: IndexPath.knowledgeBase.sony,
                element: suspended(<SonyDocumentsPage />),
                handle: { indexPath: IndexPath.knowledgeBase.root },
                children: [
                  {
                    // Nested route to open Sony Document Sheet over the Sony documents table
                    path: 'documents/:externalId',
                    element: suspended(<SonyDocumentSheet />, null),
                    handle: { indexPath: IndexPath.knowledgeBase.root },
                  },
                ],
              },
              {
                path: IndexPath.settings,
                element: suspended(<SettingsPage />),
                loader: () =>
                  defer({
                    settings: client.fetchQuery({ queryKey: ['settings'] }),
                    user: client.fetchQuery({
                      queryKey: [Endpoint.user.myself],
                      queryFn: () => UserApi.myself(),
                    }),
                  }),
                handle: { indexPath: IndexPath.settings },
              },
              {
                // Pre-SPA operator home — still arrives via bookmarks and
                // address-bar autocomplete.
                path: 'dashboard',
                element: <Navigate to=".." replace />,
              },
              {
                path: IndexPath.subscription,
                element: <RedirectToSettings tab={SettingsTabsEnum.Billing} />,
              },
              {
                path: IndexPath.user.accountSettings,
                element: <RedirectToSettings tab={SettingsTabsEnum.Organization} />,
              },
              {
                path: IndexPath.insights,
                element: suspended(<InsightsPage />),
                loader: () =>
                  defer({ settings: client.fetchQuery({ queryKey: [QueryKey.widgetSettings] }) }),
                handle: { indexPath: IndexPath.insights },
              },
              {
                path: IndexPath.identity,
                element: suspended(<IdentityPage />),
                loader: () =>
                  defer({
                    settings: client.fetchQuery({
                      queryKey: ['settings'],
                    }),
                  }),
                handle: { indexPath: IndexPath.identity },
              },
              {
                path: IndexPath.channels,
                element: suspended(<IntegrationsPage />),
                handle: { indexPath: IndexPath.channels },
              },
              {
                path: IndexPath.actions,
                element: suspended(<ActionsLoader />),
                handle: { indexPath: IndexPath.actions },
              },
              {
                path: IndexPath.simulation,
                element: suspended(<SimulationPage />),
                loader: () =>
                  defer({
                    datasets: client.fetchQuery({
                      queryKey: [QueryKey.simulation.datasets, 0, MAX_DATASETS],
                    }),
                  }),
                handle: { indexPath: IndexPath.simulation },
              },
              {
                path: IndexPath.website,
                element: suspended(<WidgetSettingsPage />),
                loader: () =>
                  defer({
                    widgetSettings: client.fetchQuery({
                      queryKey: [QueryKey.widgetSettings],
                    }),
                  }),
                handle: { indexPath: IndexPath.website },
              },
              {
                path: IndexPath.chatPage,
                element: (
                  <Navigate
                    to={`../${IndexPath.website}?${WEBSITE_VIEW_QUERY_PARAM}=${WebsiteView.ChatPage}`}
                    replace
                  />
                ),
              },
              {
                path: IndexPath.user.profile,
                element: <RedirectToSettings tab={SettingsTabsEnum.Account} />,
              },
            ],
          },
          {
            element: <Default withPadding={false} />,
            children: [
              {
                path: IndexPath.inbox.root,
                element: suspended(<InboxPage />),
                handle: {
                  indexPath: IndexPath.inbox.root,
                },
                children: [
                  {
                    path: ':id',
                    element: suspended(<Conversation />),
                    handle: {
                      indexPath: IndexPath.inbox.root,
                    },
                  },
                ],
              },
              {
                path: IndexPath.inboxBackwardCompatibilityConversation, // Backward compatibility with old links in integrations
                element: <RedirectConversationToInbox />,
              },
            ],
          },
        ],
      },
    ],
  },
];
